import * as React from "react";
import dayjs from "dayjs";

import { Box, FormControl, FormGroup, FormHelperText, Grid } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomButton from "../../../../helper-components/input/CustomButton";
import ControlledAccordion from "../../../../helper-components/form/ControlledAccordion";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { validateRow } from "../../../../helper-functions/validation/objectValidation";
import { querySelectAuthors } from "../../../../helper-functions/api/authorQueries";
import { querySelectLibraries } from "../../../../helper-functions/api/libraryQueries";
import { addFormToObj, getEllipsis, isVimeo, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { IS_RESOURCE_VALID, RESOURCE_VALIDATION } from "../../../../helper-functions/validation/resourceValidation";

import { getLanguage } from "../../../../config/language/language";
import { RESOURCE_REQUEST_OBJECT, RESOURCE_TYPES } from "../../../../config/constants/Constants";

/**
 * A form to update a resource.
 */
export default function ResourceEditForm(props) {
    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = RESOURCE_REQUEST_OBJECT;
    const validation = RESOURCE_VALIDATION;
    const isValid = IS_RESOURCE_VALID;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            // Convert to dayjs format if datetime exists.
            uploaded_on: existingRow.uploaded_on ? dayjs(existingRow.uploaded_on) : null,
            created_on: existingRow.created_on ? dayjs(existingRow.created_on) : null,
            updated_on: existingRow.updated_on ? dayjs(existingRow.updated_on) : null,

            _referer_url: existingRow.meta_data?.referer_url || null,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        if (key === "_referer_url") {
            newRow.meta_data.referer_url = value;
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleAuthors = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "author_ids", ids);
    };

    const handleLibrary = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "library_id", ids?.[0] ?? null);
    };

    const handleClick = () => {
        if (!isValid(row)) {
            props.showSnackbar({ message: getLanguage().SNACKBAR_ERROR_FORM_INVALID, color: "error" });
            return;
        }

        const options = { row };
        callback(true, options);
    };

    const accordionValues = [
        {
            title: "Authors",
            description: "Select one or more authors",
            component: (
                <CustomSelection
                    {...props}
                    multiselect
                    callback={handleAuthors}
                    query={querySelectAuthors}
                    selected={existingRow.authors}
                    getPrimarySearchText={(el) => el?.first_name}
                    getSecondarySearchText={(el) => el?.last_name}
                    getPrimaryText={(el) => `${el?.first_name} ${el?.last_name}`}
                    getSecondaryText={(el) => getEllipsis(el?.bio, 50)}
                />
            )
        },
        {
            title: "Library",
            description: "Select the library",
            component: (
                <CustomSelection
                    {...props}
                    callback={handleLibrary}
                    query={querySelectLibraries}
                    selected={existingRow.library ? [existingRow.library] : null}
                    getPrimarySearchText={(el) => el?.name}
                    getSecondarySearchText={(el) => el?.description}
                    getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                    getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                />
            )
        }
    ];

    return (
        <Box className="box">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CustomInput name="name" row={row} onChange={onChange} label="Name" />
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="description" row={row} onChange={onChange} label="Description" multiline minRows={2} maxRows={5} />
                </Grid>

                <Grid item xs={12}>
                    <CustomInput name="url" row={row} onChange={onChange} label="URL" disabled={row.url === null} />
                </Grid>

                {isVimeo(row.url) && (
                    <Grid item xs={12} mb={2}>
                        <CustomInput name="_referer_url" row={row} onChange={onChange} label="Referer URL" />
                    </Grid>
                )}

                <Grid item xs={6}>
                    <CustomInput name="type" row={row} onChange={onChange} label="Type" select={RESOURCE_TYPES} />
                </Grid>

                <Grid item xs={6}>
                    <FormControl variant="standard" sx={{ width: "100%" }}>
                        <FormGroup sx={{ alignContent: "center" }}>
                            <DateTimePicker
                                label="Uploaded on"
                                format="MM/DD/YYYY HH:mm"
                                ampm={true}
                                value={row.uploaded_on}
                                onChange={(value) => onChange(row, "uploaded_on", value)}
                                sx={{ width: "100%" }}
                            />
                        </FormGroup>
                        <FormHelperText sx={{ alignSelf: "center" }}>{row?.form?.uploaded_on?.helperText}</FormHelperText>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <ControlledAccordion {...props} startClosed values={accordionValues} />
                </Grid>
            </Grid>

            <CustomButton {...props} title={"Update resource"} onClick={handleClick} className="button" fullWidth />
        </Box>
    );
}
